import React, { useState, useEffect } from "react";
import { useNavigate, useParams, NavLink } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-awesome-modal";
import { AdminApis } from "../../apis/adminApi";
import {
  FaWhatsapp,
  FaArrowLeft,
  FaShare,
  FaShoppingCart,
  FaStore,
  FaMapMarkerAlt,
  FaShieldAlt,
  FaTruck,
  FaUndo,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaLock,
  FaMinus,
  FaPlus,
  FaFacebook,
  FaInstagram,
  FaTiktok,
  FaPhoneAlt,
  FaHome,
  FaShoppingBag,
  FaCheckCircle,
  FaTag,
} from "react-icons/fa";
import { PhoneInput } from "react-contact-number-input";
import { Oval } from "react-loader-spinner";
import configs from "../../configs";
import { PaymentApis } from "../../apis/paymentApis";

export default function CardViewProductDetailsPage() {
  const navigate = useNavigate();
  const params = useParams();

  const [data, setData] = useState({});
  const [marketInfo, setMarketInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isScrolled, setIsScrolled] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    quantity: 1,
  });

  const productImages = [data?.product_image_1, data?.product_image_2, data?.product_image_3].filter(
    (img) => img && img !== "no image"
  );

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { fetchProductData(); }, [params.storeId]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      const res = await AdminApis.getSingleProductOutside(params?.storeId);
      if (res?.data) {
        setData(res.data.data.product);
        setMarketInfo(res.data.data.market_info);
      }
    } catch {
      toast.error("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    const paymentData = {
      user_id: data?.user_id,
      amount: data?.product_price * paymentForm.quantity,
      customer_full_name: paymentForm.fullName,
      product_qty: paymentForm.quantity + "",
      pay_for: data?.product_name,
      store_id: data?.link_name,
      customer_email: paymentForm.email,
      customer_phone_number:
        paymentForm.phoneNumber?.countryCode + paymentForm.phoneNumber?.phoneNumber,
    };
    try {
      const res = await PaymentApis.payForProduct(paymentData);
      if (res?.data?.success) {
        window.location.replace(res.data.data.data.link);
        setPaymentModal(false);
      }
    } catch {
      toast.error("Payment failed. Please try again.");
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(price || 0);

  const theme = {
    primary: marketInfo?.brand_primary_color || "#2563EB",
    primaryLight: marketInfo?.brand_primary_color ? `${marketInfo.brand_primary_color}15` : "#EFF6FF",
  };

  const storeName = data?.link_name?.replace(/-/g, " ");
  const hasDiscount =
    data?.no_of_items && parseFloat(data.no_of_items) < parseFloat(data.product_price);
  const discountPct = hasDiscount
    ? Math.round((1 - parseFloat(data.product_price) / parseFloat(data.no_of_items)) * 100)
    : 0;

  const nextImage = () =>
    setCurrentImageIndex((p) => (p === productImages.length - 1 ? 0 : p + 1));
  const prevImage = () =>
    setCurrentImageIndex((p) => (p === 0 ? productImages.length - 1 : p - 1));

  const shareProduct = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: data?.product_name, text: data?.product_description, url: window.location.href }); }
      catch {}
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Product link copied!");
    }
  };

  /* ─── Loading ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Oval height={48} width={48} color="#2563EB" secondaryColor="#E5E7EB" strokeWidth={3} />
          <p className="mt-4 font-semibold text-gray-900">Loading Product</p>
          <p className="text-sm text-gray-400 mt-1">Please wait…</p>
        </div>
      </div>
    );
  }

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-[#f8fafc]">

      {/* ── Sticky Header ─────────────────────────────────────────────── */}
      <header
        className={`transition-all duration-300 ${isScrolled ? "sticky top-0 z-50 shadow-md" : ""}`}
        style={{ backgroundColor: theme.primary }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-3.5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
              >
                <FaArrowLeft size={16} />
              </button>
              <NavLink to={`/s/${data?.link_name}`} className="flex items-center gap-2.5">
                {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
                  <img src={marketInfo.brand_logo} alt="logo" className="w-9 h-9 object-contain rounded-lg bg-white p-1" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-white">
                    <FaStore size={15} />
                  </div>
                )}
                <div className="hidden sm:block">
                  <p className="font-bold text-white text-sm capitalize leading-tight">{storeName}</p>
                  <p className="text-white/60 text-[10px]">View all products</p>
                </div>
              </NavLink>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={shareProduct}
                className="w-9 h-9 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
              >
                <FaShare size={15} />
              </button>
              {marketInfo?.phone_number && (
                <a
                  href={`${configs?.baseRedirect}/${marketInfo.phone_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white text-gray-900 text-xs font-bold rounded-full hover:bg-gray-100 transition-colors"
                >
                  <FaWhatsapp size={14} className="text-green-500" /> Contact Store
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ── Breadcrumb ────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-xs text-gray-400">
            <NavLink to={`/s/${data?.link_name}`} className="hover:text-gray-700 transition-colors">
              <FaHome size={12} />
            </NavLink>
            <span>/</span>
            <NavLink to={`/s/${data?.link_name}`} className="hover:text-gray-700 transition-colors capitalize">
              {storeName}
            </NavLink>
            <span>/</span>
            <span className="text-gray-700 font-semibold capitalize truncate max-w-[200px]">{data?.product_name}</span>
          </nav>
        </div>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <main className="max-w-7xl mx-auto px-4 py-8 pb-32 lg:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14">

          {/* ── Image Gallery ──────────────────────────────────────── */}
          <div className="space-y-3">
            {/* Main image */}
            <div className="relative bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 aspect-square">
              {productImages.length > 0 ? (
                <>
                  <img
                    src={productImages[currentImageIndex]}
                    alt={data?.product_name}
                    className="w-full h-full object-cover"
                  />

                  {/* Arrows */}
                  {productImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all"
                      >
                        <FaChevronLeft size={13} className="text-gray-700" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all"
                      >
                        <FaChevronRight size={13} className="text-gray-700" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-2.5 py-1 bg-black/50 text-white text-xs font-bold rounded-full">
                        {currentImageIndex + 1} / {productImages.length}
                      </div>
                    </>
                  )}

                  {/* Badges */}
                  {hasDiscount && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-red-500 text-white text-xs font-extrabold rounded-full shadow">
                      {discountPct}% OFF
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-300">
                  <FaShoppingBag size={56} />
                  <p className="text-sm mt-3 text-gray-400">No image available</p>
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {productImages.length > 1 && (
              <div className="grid grid-cols-3 gap-2.5">
                {productImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentImageIndex(i)}
                    className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                      i === currentImageIndex ? "shadow-md" : "border-gray-200 hover:border-gray-400 opacity-70 hover:opacity-100"
                    }`}
                    style={i === currentImageIndex ? { borderColor: theme.primary } : {}}
                  >
                    <img src={img} alt={`${data?.product_name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Product Info ───────────────────────────────────────── */}
          <div className="space-y-5">

            {/* Title + Price Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">

              {/* Badges row */}
              <div className="flex flex-wrap gap-2 mb-4">
                {data?.category && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">
                    <FaTag size={9} /> {data.category}
                  </span>
                )}
                {data?.location && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">
                    <FaMapMarkerAlt size={9} /> {data.location}
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 leading-snug capitalize mb-5">
                {data?.product_name}
              </h1>

              {/* Price */}
              <div className="flex items-baseline flex-wrap gap-3 mb-6">
                <span className="text-3xl font-extrabold" style={{ color: theme.primary }}>
                  {formatPrice(data?.product_price)}
                </span>
                {hasDiscount && (
                  <>
                    <span className="text-xl text-gray-400 line-through font-medium">
                      {formatPrice(data.no_of_items)}
                    </span>
                    <span className="px-2.5 py-1 bg-red-100 text-red-600 text-xs font-extrabold rounded-lg">
                      Save {discountPct}%
                    </span>
                  </>
                )}
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <p className="text-sm font-bold text-gray-700 mb-3">Quantity</p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <FaMinus size={11} />
                    </button>
                    <span className="w-14 text-center font-extrabold text-lg border-x border-gray-200">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-11 h-11 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      <FaPlus size={11} />
                    </button>
                  </div>
                  <p className="text-sm text-gray-500">
                    Total:{" "}
                    <span className="font-extrabold text-base" style={{ color: theme.primary }}>
                      {formatPrice(data?.product_price * quantity)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Action buttons — hidden on mobile (shown in sticky bar) */}
              <div className="hidden lg:flex flex-col gap-3">
                <button
                  onClick={() => { setPaymentForm((p) => ({ ...p, quantity })); setPaymentModal(true); }}
                  className="w-full py-4 text-white rounded-xl font-extrabold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2.5"
                  style={{ backgroundColor: theme.primary }}
                >
                  <FaShoppingCart size={16} />
                  Buy Now — {formatPrice(data?.product_price * quantity)}
                </button>
                <a
                  href={`${configs?.baseRedirect}/${data?.phone_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full py-4 bg-green-500 text-white rounded-xl font-extrabold text-base hover:bg-green-600 transition-colors flex items-center justify-center gap-2.5 shadow"
                >
                  <FaWhatsapp size={18} /> Chat with Seller
                </a>
              </div>
            </div>

            {/* Trust badges */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <div className="grid grid-cols-3 gap-4">
                {[
                  { Icon: FaShieldAlt, bg: "bg-emerald-100", color: "text-emerald-600", label: "Secure", sub: "100% Protected" },
                  { Icon: FaTruck, bg: "bg-blue-100", color: "text-blue-600", label: "Fast Delivery", sub: "Quick Shipping" },
                  { Icon: FaUndo, bg: "bg-purple-100", color: "text-purple-600", label: "Easy Returns", sub: "Hassle Free" },
                ].map(({ Icon, bg, color, label, sub }) => (
                  <div key={label} className="text-center">
                    <div className={`w-11 h-11 mx-auto ${bg} rounded-xl flex items-center justify-center mb-2`}>
                      <Icon className={color} size={17} />
                    </div>
                    <p className="text-xs font-bold text-gray-900">{label}</p>
                    <p className="text-[10px] text-gray-500">{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            {data?.product_description && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-base font-extrabold text-gray-900 mb-3">Product Description</h3>
                <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                  {data.product_description}
                </p>
              </div>
            )}

            {/* Sold by */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
              <h3 className="text-sm font-extrabold text-gray-900 mb-3">Sold by</h3>
              <NavLink
                to={`/s/${data?.link_name}`}
                className="flex items-center gap-3.5 p-3.5 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group"
              >
                {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
                  <img src={marketInfo.brand_logo} alt="logo" className="w-12 h-12 object-contain rounded-xl border border-gray-200" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: theme.primary }}>
                    <FaStore size={20} />
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="font-extrabold text-gray-900 capitalize text-sm">{storeName}</p>
                    <FaCheckCircle className="text-blue-500" size={13} />
                  </div>
                  <p className="text-xs text-gray-400 group-hover:text-gray-600 transition-colors mt-0.5">
                    View all products →
                  </p>
                </div>
              </NavLink>
            </div>
          </div>
        </div>
      </main>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-white mt-6">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {marketInfo?.brand_logo && marketInfo.brand_logo !== "no image" ? (
                  <img src={marketInfo.brand_logo} alt="logo" className="w-12 h-12 object-contain rounded-xl bg-white p-1.5" />
                ) : (
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: theme.primary }}>
                    <FaStore size={20} />
                  </div>
                )}
                <div>
                  <h4 className="text-lg font-extrabold capitalize">{storeName}</h4>
                  <p className="text-gray-400 text-sm">Official Store</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-5 max-w-sm">
                {marketInfo?.brand_description || "Your trusted destination for quality products at great prices."}
              </p>
              <div className="flex gap-2.5">
                {[
                  { url: marketInfo?.facebook_url, Icon: FaFacebook, hover: "hover:bg-blue-600" },
                  { url: marketInfo?.instagram_url, Icon: FaInstagram, hover: "hover:bg-pink-600" },
                  { url: marketInfo?.tiktok_url, Icon: FaTiktok, hover: "hover:bg-gray-700" },
                  { url: data?.phone_number ? `${configs?.baseRedirect}/${data.phone_number}` : null, Icon: FaWhatsapp, hover: "hover:bg-green-600" },
                ]
                  .filter((s) => s.url)
                  .map(({ url, Icon, hover }, i) => (
                    <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                      className={`w-9 h-9 bg-gray-800 ${hover} rounded-full flex items-center justify-center transition-colors`}>
                      <Icon size={15} />
                    </a>
                  ))}
              </div>
            </div>

            <div>
              <h5 className="font-bold text-xs uppercase tracking-wider text-gray-500 mb-4">Quick Links</h5>
              <ul className="space-y-2.5 text-sm text-gray-400">
                <li><NavLink to={`/s/${data?.link_name}`} className="hover:text-white transition-colors">All Products</NavLink></li>
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h5 className="font-bold text-xs uppercase tracking-wider text-gray-500 mb-4">Contact</h5>
              <ul className="space-y-2.5 text-sm text-gray-400">
                {data?.phone_number && (
                  <li className="flex items-center gap-2.5">
                    <FaPhoneAlt size={12} className="flex-shrink-0" /> {data.phone_number}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-gray-500 text-xs">© {new Date().getFullYear()} <span className="capitalize">{storeName}</span>. All rights reserved.</p>
            <a href="https://www.mygupta.co" target="_blank" rel="noopener noreferrer"
              className="text-gray-500 hover:text-white text-xs transition-colors">
              Powered by <span className="font-bold text-white">Gupta</span>
            </a>
          </div>
        </div>
      </footer>

      {/* ── Mobile Sticky Bottom Bar ───────────────────────────────────── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-2.5">
          <a
            href={`${configs?.baseRedirect}/${data?.phone_number}`}
            target="_blank"
            rel="noopener noreferrer"
            className="w-13 h-13 flex items-center justify-center bg-green-500 text-white rounded-xl flex-shrink-0 hover:bg-green-600 transition-colors p-3"
          >
            <FaWhatsapp size={22} />
          </a>
          <button
            onClick={() => { setPaymentForm((p) => ({ ...p, quantity })); setPaymentModal(true); }}
            className="flex-1 h-13 py-3.5 text-white rounded-xl font-extrabold text-sm shadow-lg transition-all flex items-center justify-center gap-2"
            style={{ backgroundColor: theme.primary }}
          >
            <FaShoppingCart size={15} />
            Buy Now — {formatPrice(data?.product_price * quantity)}
          </button>
        </div>
      </div>

      {/* ── Payment Modal ─────────────────────────────────────────────── */}
      <Modal
        visible={paymentModal}
        width="460"
        height="620"
        effect="fadeInUp"
        onClickAway={() => setPaymentModal(false)}
      >
        <div className="h-full flex flex-col bg-white rounded-2xl overflow-hidden">
          <div className="px-5 py-4 text-white flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}cc)` }}>
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-extrabold">Checkout</h2>
                <p className="text-white/75 text-xs mt-0.5">Complete your purchase securely</p>
              </div>
              <button onClick={() => setPaymentModal(false)}
                className="p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-full transition-colors">
                <FaTimes size={15} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {/* Product preview */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              {productImages[0] ? (
                <img src={productImages[0]} alt={data?.product_name} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaShoppingBag className="text-gray-400" size={20} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-gray-900 text-sm truncate">{data?.product_name}</p>
                <p className="text-base font-extrabold mt-0.5" style={{ color: theme.primary }}>
                  {formatPrice(data?.product_price)}
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="space-y-3">
              {[
                { label: "Full Name", type: "text", key: "fullName", placeholder: "Your full name" },
                { label: "Email Address", type: "email", key: "email", placeholder: "you@email.com" },
              ].map(({ label, type, key, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                  <input
                    type={type}
                    required
                    value={paymentForm[key]}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Phone Number</label>
                <PhoneInput required countryCode="ng"
                  onChange={(phone) => setPaymentForm((p) => ({ ...p, phoneNumber: phone }))}
                  placeholder="Phone number" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Quantity</label>
                <div className="flex items-center gap-3">
                  <button type="button"
                    onClick={() => setPaymentForm((p) => ({ ...p, quantity: Math.max(1, p.quantity - 1) }))}
                    className="w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center text-lg font-bold transition-colors">−</button>
                  <span className="w-14 text-center font-extrabold text-lg">{paymentForm.quantity}</span>
                  <button type="button"
                    onClick={() => setPaymentForm((p) => ({ ...p, quantity: p.quantity + 1 }))}
                    className="w-10 h-10 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center justify-center text-lg font-bold transition-colors">+</button>
                </div>
              </div>
            </div>

            {/* Order total */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-gray-500">Unit price</span>
                <span className="font-medium">{formatPrice(data?.product_price || 0)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-gray-500">Quantity</span>
                <span className="font-medium">× {paymentForm.quantity}</span>
              </div>
              <div className="border-t border-gray-200 mt-2 pt-2.5 flex justify-between">
                <span className="font-extrabold text-gray-900">Total</span>
                <span className="text-xl font-extrabold" style={{ color: theme.primary }}>
                  {formatPrice((data?.product_price || 0) * paymentForm.quantity)}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-center gap-1.5 text-gray-400 text-xs">
              <FaLock size={10} /> Secured by Flutterwave
            </div>
          </div>

          <form onSubmit={handlePayment} className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
            <button type="submit"
              className="w-full py-3.5 text-white font-extrabold text-sm rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              style={{ backgroundColor: theme.primary }}>
              <FaLock size={12} />
              Pay {formatPrice((data?.product_price || 0) * paymentForm.quantity)}
            </button>
          </form>
        </div>
      </Modal>

      <ToastContainer position="bottom-right" autoClose={3000} theme="light" toastStyle={{ borderRadius: "12px" }} />
    </div>
  );
}
